/**
 * The call times Book a demo offers: a weekday in the next 60 days, on the
 * half hour from 09:00 to 18:30, in the visitor's own time zone. Staff send a
 * Google Meet invite for the slot picked, so it has to be a real instant.
 */

export const DEMO_DAYS_AHEAD = 60;
const FIRST_SLOT = 9 * 60;
const LAST_SLOT = 18 * 60 + 30;
const STEP = 30;
/** No slot sooner than this: someone has to see the request and send the invite. */
const LEAD_MS = 2 * 60 * 60 * 1000;

const pad = (n: number) => String(n).padStart(2, '0');

/** A local date as YYYY-MM-DD, the value of an <input type="date">. */
export function isoDay(d: Date): string {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function firstDay(now: Date = new Date()): string {
    return isoDay(now);
}

export function lastDay(now: Date = new Date()): string {
    const d = new Date(now);
    d.setDate(d.getDate() + DEMO_DAYS_AHEAD);
    return isoDay(d);
}

/** The local instant for a picked day and slot. */
export function slotInstant(day: string, time: string): Date | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || !/^\d{2}:\d{2}$/.test(time)) return null;
    const at = new Date(`${day}T${time}:00`);
    return Number.isFinite(at.getTime()) ? at : null;
}

export function isWeekday(day: string): boolean {
    const at = slotInstant(day, '12:00');
    if (!at) return false;
    const dow = at.getDay();
    return dow !== 0 && dow !== 6;
}

/** The slots still open on `day` ("09:00", "09:30", …): none on a weekend, none in the past. */
export function slotsFor(day: string, now: Date = new Date()): string[] {
    if (!isWeekday(day) || day < firstDay(now) || day > lastDay(now)) return [];
    const out: string[] = [];
    for (let m = FIRST_SLOT; m <= LAST_SLOT; m += STEP) {
        const time = `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
        const at = slotInstant(day, time);
        if (at && at.getTime() >= now.getTime() + LEAD_MS) out.push(time);
    }
    return out;
}

/** Busy time from the API (ISO instants): calls already booked, merged, with nothing about who booked them. */
export interface BusyRange {
    start: string;
    end: string;
}

/** A call takes up half an hour; a slot that overlaps a booked call is taken too (ARDE-288). */
export const CALL_MS = 30 * 60 * 1000;

/** The instants a local day starts and ends at, to ask the API which of its times are taken. */
export function dayBounds(day: string): { from: string; to: string } | null {
    const start = slotInstant(day, '00:00');
    if (!start) return null;
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return { from: start.toISOString(), to: end.toISOString() };
}

/** The slots on `day` someone can still book: open (slotsFor) and clear of every booked call. */
export function freeSlots(day: string, busy: BusyRange[], now: Date = new Date()): string[] {
    const taken = busy.map((b) => [Date.parse(b.start), Date.parse(b.end)]).filter(([s, e]) => Number.isFinite(s) && Number.isFinite(e));
    return slotsFor(day, now).filter((time) => {
        const at = slotInstant(day, time)?.getTime();
        return at !== undefined && !taken.some(([s, e]) => at < e && at + CALL_MS > s);
    });
}

/** The visitor's IANA zone (Europe/Paris), or undefined when the browser will not say. */
export function localTimeZone(): string | undefined {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    } catch {
        return undefined;
    }
}

/** "Tuesday 7 October, 10:30" (or "Tue 7 Oct, 10:30" when `short`), in the page's language. */
export function formatSlot(at: Date, locale: string, timeZone?: string, short = false): string {
    try {
        const width = short ? 'short' : 'long';
        return new Intl.DateTimeFormat(locale, { weekday: width, day: 'numeric', month: width, hour: '2-digit', minute: '2-digit', ...(timeZone ? { timeZone } : {}) }).format(at);
    } catch {
        return at.toLocaleString();
    }
}

/**
 * A Google Calendar "new event" link for the requested slot: the requester as
 * guest, 30 minutes. Calendar adds the Meet link itself when the account has
 * "Automatically add Google Meet" on (the default on Workspace).
 */
export function meetInviteUrl(input: { at: Date; email: string; title: string; details: string; minutes?: number }): string {
    const stamp = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
    const end = new Date(input.at.getTime() + (input.minutes ?? 30) * 60 * 1000);
    const q = new URLSearchParams({
        action: 'TEMPLATE',
        text: input.title,
        dates: `${stamp(input.at)}/${stamp(end)}`,
        add: input.email,
        details: input.details,
    });
    return `https://calendar.google.com/calendar/render?${q.toString()}`;
}
